import type { CoCustomerTimeSeriesPoint } from 'src/services/coCustomerAnalyticsService';
import { AnalyticsCard } from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCard/AnalyticsCard';
import { AnalyticsCustomerPopup } from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCustomerPopup/AnalyticsCustomerPopup';
import { ResolutionRateCard } from 'src/pages/AnalyticsPage/AnalyticsCustomer/ResolutionRateCard/ResolutionRateCard';
import { TotalProcessedTicketsCard } from 'src/pages/AnalyticsPage/AnalyticsCustomer/TotalProcessedTicketsCard/TotalProcessedTicketsCard';
import { DateItemsGroupByX } from 'src/pages/AnalyticsPage/AnalyticsCustomer/DateItems';
import { cardMetric, cardTitle, formatNum, trendOf } from '../utils/metricFormatters';
import { DateRangeItem, MetricValue, NumericMetricValue } from '../AnalyticsCoCustomer.types';

type AiPerformanceOverviewSectionProps = {
  dateRange: DateRangeItem;
  resolutionRate: MetricValue;
  resolvedTickets: NumericMetricValue;
  escalatedTickets: NumericMetricValue;
  inProgressTickets: MetricValue;
  totalProcessedTickets: MetricValue;
  aiRepliesSent: MetricValue;
  aiRepliesPerProcessedTicket: MetricValue;
  timeSeriesResponse: CoCustomerTimeSeriesPoint[];
};

export const AiPerformanceOverviewSection = ({
  dateRange,
  resolutionRate,
  resolvedTickets,
  escalatedTickets,
  inProgressTickets,
  totalProcessedTickets,
  aiRepliesSent,
  aiRepliesPerProcessedTicket,
  timeSeriesResponse,
}: AiPerformanceOverviewSectionProps) => {
  const ticketCounters = [
    { key: 'resolved', title: 'Total resolved tickets', metric: resolvedTickets },
    { key: 'escalated', title: 'Total escalated tickets', metric: escalatedTickets },
    {
      key: 'inProgress',
      title: 'Total in-progress tickets',
      metric: inProgressTickets,
      tooltip: (
        <span>Total number of tickets currently being handled by AI and not yet resolved.</span>
      ),
    },
  ];

  return (
    <>
      <div className="flex flex-row md:gap-[10px] gap-[6px] flex-wrap">
        <div className="flex flex-[32%] min-w-[200px]">
          <AnalyticsCard
            title={cardTitle('Resolution Rate')}
            metric={cardMetric(resolutionRate.value === 'N/A' ? 'N/A' : `${resolutionRate.value}%`)}
            trend={trendOf(resolutionRate.percentage)}
          />
        </div>
        <div className="flex flex-[66%] min-w-[300px] flex-row md:gap-[10px] gap-[6px] flex-wrap">
          {ticketCounters.map(({ key, title, metric, tooltip }) => (
            <div className="flex flex-1 min-w-[140px]" key={key}>
              <AnalyticsCard
                title={cardTitle(title)}
                metric={cardMetric(formatNum(metric.value))}
                trend={trendOf(metric.percentage)}
                toolTipDescription={tooltip}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-row md:gap-[10px] gap-[6px] flex-wrap">
        <div className="flex flex-[32%] min-w-[280px]">
          <ResolutionRateCard
            title={'Resolution rate trend'}
            showRate={false}
            className={'h-full'}
            resolved={resolvedTickets.value}
            escalated={escalatedTickets.value}
            tooltipDescription={'Total Resolved Tickets / Total Processed Tickets'}
            icon={
              <AnalyticsCustomerPopup
                title={'Resolution rate'}
                dateRange={dateRange}
                dataKey={'resolutionRate'}
                chartType={'lineChart'}
                className={''}
                xAxisSelect={DateItemsGroupByX}
              />
            }
          />
        </div>
        <div className="flex flex-col flex-[66%] min-w-[320px] md:gap-[10px] gap-[6px]">
          <TotalProcessedTicketsCard
            showHeaderStats={false}
            showLegend
            tooltipDescription={'Total Resolved + Total Escalated'}
            resolved={resolvedTickets.value}
            escalated={escalatedTickets.value}
            timeSeriesResponse={timeSeriesResponse}
            total={totalProcessedTickets.value}
            icon={
              <AnalyticsCustomerPopup
                popupTitle={'Total processed tickets'}
                title={'Resolved'}
                stackTitle={'Escalated'}
                dateRange={dateRange}
                dataKey={'resolvedTickets'}
                dataKeyStack={'escalatedTickets'}
                chartType={'barChart'}
                className={''}
                xAxisSelect={DateItemsGroupByX}
              />
            }
          />
          <div className="flex flex-row md:gap-[10px] gap-[6px] flex-wrap">
            <div className="flex flex-1 min-w-[200px]">
              <AnalyticsCard
                title={cardTitle('Total AI replies sent')}
                metric={cardMetric(formatNum(aiRepliesSent.value))}
                trend={trendOf(aiRepliesSent.percentage)}
                icon={
                  <AnalyticsCustomerPopup
                    title={'Total AI replies sent'}
                    dateRange={dateRange}
                    dataKey={'aiRepliesSent'}
                    chartType={'barChart'}
                    className={''}
                    xAxisSelect={DateItemsGroupByX}
                  />
                }
                toolTipDescription={
                  <span>Total number of AI replies sent in processed and in-progress tickets</span>
                }
              />
            </div>
            <div className="flex flex-1 min-w-[200px]">
              <AnalyticsCard
                title={cardTitle('Average AI replies per ticket')}
                metric={cardMetric(
                  aiRepliesPerProcessedTicket.value === 'N/A'
                    ? 'N/A'
                    : `${aiRepliesPerProcessedTicket.value} replies`,
                )}
                icon={
                  <AnalyticsCustomerPopup
                    title={'Average AI replies per ticket'}
                    dateRange={dateRange}
                    dataKey={'aiRepliesPerProcessedTicket'}
                    chartType={'barChart'}
                    className={''}
                    xAxisSelect={DateItemsGroupByX}
                  />
                }
                toolTipDescription={
                  <span>AI replies sent in processed ticket / Total processed tickets</span>
                }
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
